import { createTheme } from '@mui/material/styles';

// assets
import colors from 'assets/scss/_themes-vars.module.scss';

// project imports
import componentStyleOverrides from './compStyleOverride';
import themePalette from './palette';
import themeTypography from './typography';

/**
 * Represent theme style and structure as per Material-UI
 * @param {JsonObject} customization customization parameter object
 */

export const theme = (customization) => {
  const color = colors;

  const themeOption = {
    colors: color,
    heading: color.grey900,
    paper: color.paper,
    backgroundDefault: color.paper,
    background: color.primaryLight,
    darkTextPrimary: color.grey700,
    darkTextSecondary: color.grey500,
    textDark: color.grey900,
    menuSelected: color.secondaryDark,
    menuSelectedBack: color.secondaryLight,
    divider: color.grey200,
    customization
  };

  const themeOptions = {
    direction: 'ltr',
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 768,
        lg: 1024,
        xl: 1280,
        '2xl': 1536,
      },
    },
    palette: themePalette(themeOption),
    mixins: {
      toolbar: {
        minHeight: '44px',
        padding: '16px',
        '@media (min-width: 600px)': {
          minHeight: '44px'
        },
        '@media (max-width: 640px)': {
          minHeight: '44px',
          padding: '12px'
        }
      }
    },
    typography: themeTypography(themeOption),
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 12,
            minHeight: 44,
            fontWeight: 600,
            fontSize: '16px',
            padding: '12px 24px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            },
            '@media (max-width: 640px)': {
              minHeight: 44,
              fontSize: '16px',
              padding: '12px 20px',
              borderRadius: 10
            }
          },
          contained: {
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
            }
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(0, 0, 0, 0.04)',
            '@media (max-width: 640px)': {
              borderRadius: 12
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              borderRadius: 12,
              fontSize: '16px',
              '@media (max-width: 640px)': {
                minHeight: 44,
                borderRadius: 10
              }
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.12)',
                borderWidth: '1.5px'
              },
              '&:hover fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.24)'
              },
              '&.Mui-focused fieldset': {
                borderColor: '#007AFF',
                borderWidth: '2px'
              }
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            '@media (max-width: 640px)': {
              minHeight: 32,
              fontSize: '14px'
            }
          }
        }
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            '@media (max-width: 640px)': {
              width: 40,
              height: 40
            }
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            '@media (max-width: 640px)': {
              minWidth: 44,
              minHeight: 44
            }
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(20px)',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none'
          }
        }
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: 'rgba(0, 0, 0, 0.06)'
          }
        }
      }
    }
  };

  const themes = createTheme(themeOptions);
  themes.components = componentStyleOverrides(themeOption);

  return themes;
};

export default theme;
